import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Plus, Star, Trash2 } from 'lucide-react';
import type { ClientContact, ClientDetail, ContactInput } from '../../lib/clientsApi';
import { formatCurrency } from '../../lib/format';
import { useCreateContact, useDeleteContact } from '../../hooks/useClients';
import { useClientStats } from '../../hooks/useEntityStats';
import { useLeads } from '../../hooks/useLeads';
import { useModules } from '../../hooks/useModules';
import { usePermissions } from '../../hooks/usePermissions';
import { FinancialStatsSection } from '../financials/FinancialStatsSection';

export function ClientOverviewTab({ client }: { client: ClientDetail }) {
  const statsQuery = useClientStats(client.id);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <InfoCard client={client} />
          <ContactsCard clientId={client.id} contacts={client.contacts} />
          {client.notes && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="mb-2 text-sm font-semibold text-gray-900">Notițe</h3>
              <p className="whitespace-pre-wrap text-sm text-gray-700">{client.notes}</p>
            </div>
          )}
        </div>
        <div className="space-y-6">
          <OnboardingCard clientId={client.id} />
          <FinancialsCard client={client} />
        </div>
      </div>
      {/* Full-width financial stats (KPI + monthly chart + transactions). */}
      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Statistici financiare</h3>
        <FinancialStatsSection
          stats={statsQuery.data}
          isLoading={statsQuery.isLoading}
          direction="in"
        />
      </div>
    </div>
  );
}

function InfoCard({ client }: { client: ClientDetail }) {
  const rows: { label: string; value: string | null }[] = [
    { label: 'Industrie', value: client.industry?.name ?? null },
    { label: 'CIF / Cod TVA', value: client.cifCui },
    { label: 'Reg. Com.', value: client.regCom },
    { label: 'Email', value: client.email },
    { label: 'Telefon', value: client.phone },
    { label: 'Website', value: client.website },
    { label: 'Adresă', value: client.address },
    { label: 'Adresă de facturare', value: client.billingAddress },
  ];
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">Detalii companie</h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.label} className="flex flex-col">
            <dt className="text-xs text-gray-500">{r.label}</dt>
            <dd className="text-sm text-gray-800">{r.value || '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Where the client stands in onboarding: the cards of the Onboarding pipeline
 * linked to it (a lead won in sales becomes a client and lands there).
 */
function OnboardingCard({ clientId }: { clientId: string }) {
  const { isOn } = useModules();
  const { can } = usePermissions();
  const enabled = isOn('leads') && can('leads', 'canView');
  const { data } = useLeads({ clientId, pipelineKind: 'ONBOARDING', pageSize: 5 }, enabled);
  const leads = data?.leads ?? [];
  if (!enabled || leads.length === 0) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">Onboarding</h3>
      <ul className="space-y-2">
        {leads.map((l) => (
          <li key={l.id} className="flex items-center justify-between gap-3 text-sm">
            <Link to={`/leads/${l.id}`} className="min-w-0 truncate text-brand-700 hover:underline">
              {l.title}
            </Link>
            <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-gray-600">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: l.stage?.color ?? '#6b6b75' }}
              />
              {l.stage?.name ?? '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FinancialsCard({ client }: { client: ClientDetail }) {
  const f = client.financials;
  const stats: { label: string; value: string; tone?: string }[] = [
    { label: 'Total facturat', value: formatCurrency(f.totalInvoiced, f.currency) },
    {
      label: 'Total încasat',
      value: formatCurrency(f.totalPaid, f.currency),
      tone: 'text-green-700',
    },
    {
      label: 'De încasat',
      value: formatCurrency(f.outstanding, f.currency),
      tone: f.outstanding > 0 ? 'text-amber-700' : 'text-gray-500',
    },
  ];
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <h3 className="mb-3 text-sm font-semibold text-gray-900">Financiar</h3>
      <div className="space-y-3">
        {stats.map((s) => (
          <div key={s.label} className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{s.label}</span>
            <span className={`text-sm font-semibold ${s.tone ?? 'text-gray-900'}`}>{s.value}</span>
          </div>
        ))}
        <div className="border-t border-gray-100 pt-3 text-xs text-gray-400">
          {client.counts.projects} proiecte · {client.counts.meetingNotes} notițe ·{' '}
          {client.counts.resources} resurse
        </div>
      </div>
    </div>
  );
}

function ContactsCard({ clientId, contacts }: { clientId: string; contacts: ClientContact[] }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState<ContactInput>({ name: '', isPrimary: false });
  const createMutation = useCreateContact(clientId);
  const deleteMutation = useDeleteContact(clientId);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    await createMutation.mutateAsync({
      name: form.name.trim(),
      role: form.role?.trim() || undefined,
      email: form.email?.trim() || undefined,
      phone: form.phone?.trim() || undefined,
      isPrimary: form.isPrimary,
    });
    setForm({ name: '', isPrimary: false });
    setAdding(false);
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Persoane de contact</h3>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className="inline-flex items-center gap-1 text-sm text-brand-700 hover:underline"
        >
          <Plus size={14} /> Adaugă
        </button>
      </div>

      {contacts.length === 0 && !adding && (
        <p className="text-sm text-gray-500">Niciun contact înregistrat.</p>
      )}

      <ul className="divide-y divide-gray-100">
        {contacts.map((c) => (
          <li key={c.id} className="flex items-center justify-between py-2">
            <div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                {c.isPrimary && <Star size={13} className="text-amber-500" fill="currentColor" />}
                {c.name}
                {c.role && <span className="text-xs font-normal text-gray-500">· {c.role}</span>}
              </div>
              <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                {c.email && (
                  <span className="inline-flex items-center gap-1">
                    <Mail size={11} /> {c.email}
                  </span>
                )}
                {c.phone && (
                  <span className="inline-flex items-center gap-1">
                    <Phone size={11} /> {c.phone}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => deleteMutation.mutate(c.id)}
              className="text-gray-300 hover:text-red-500"
              aria-label="Șterge contactul"
            >
              <Trash2 size={15} />
            </button>
          </li>
        ))}
      </ul>

      {adding && (
        <form onSubmit={add} className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <input
            placeholder="Nume *"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <input
            placeholder="Rol"
            value={form.role ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <input
            placeholder="Email"
            type="email"
            value={form.email ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <input
            placeholder="Telefon"
            value={form.phone ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
          />
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              checked={form.isPrimary ?? false}
              onChange={(e) => setForm((p) => ({ ...p, isPrimary: e.target.checked }))}
            />
            Contact principal
          </label>
          <div className="flex justify-end gap-2 sm:col-span-2">
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Salvează
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
